import { values } from 'lodash';

import { DailyBudgetMetrikaOperationsParams } from '../../actions/campaignsEdit';
import {
    isContextPlatformsStrategy,
    isDefaultStrategyHasContextSettings,
    isDefaultStrategyHasSeparateBidding
} from '../../service/StrategyEditorHelpers/StrategyEditorHelpers';
import { EditCampaignData, EditCampaignsConstants, EditCampaignValueField } from '../../typings/campaignsEdit';
import { CampaignId, CampaignPlatform } from '../../typings/domain';
import { AdditionalGoalIds } from '../../typings/goals';
import {
    GdCampaignAttributionModel,
    GdCampaignAttributionModelInput,
    GdCampaignBiddingStrategyInput,
    GdCampaignBudgetShowMode,
    GdCampaignPlatformInput,
    GdCampaignStrategyDataInput,
    GdCampaignStrategyInput,
    GdCampaignStrategyNameInput,
    GdDayBudgetShowModeInput
} from '../../typings/graphql-grid';
import {
    AttributionModel,
    AutobudgetStrategy,
    DnaStrategyType,
    OptimizationCriteria,
    Strategy,
    StrategyManual,
    StrategyOptimizeClicks,
    StrategyOptimizeCpa,
    StrategyRoi
} from '../../typings/strategy';
import { getNumberForApiFormat } from '../common/common';

export function mapDayBudgetToApiFormat(strategy: Strategy, defaultValue: number): string {
    if (strategy.type !== DnaStrategyType.MANUAL || !strategy.dailyBudget) {
        // дневной бюджет есть только у ручной стратегии
        return String(defaultValue);
    }
    return String(strategy.dailyBudget);
}

export function mapDayBudgetShowModeToApiFormat(strategy: Strategy): GdDayBudgetShowModeInput {
    if (strategy.type !== DnaStrategyType.MANUAL || !strategy.dailyBudget) {
        // дневной бюджет есть только у ручной стратегии
        return GdDayBudgetShowModeInput.DEFAULT_;
    }

    switch (strategy.dailyBudgetMode) {
        case GdCampaignBudgetShowMode.STRETCHED:
            return GdDayBudgetShowModeInput.STRETCHED;

        case GdCampaignBudgetShowMode.DEFAULT:
        default:
            return GdDayBudgetShowModeInput.DEFAULT_;
    }
}

export function mapEnableCpcHoldToApiFormat(strategy: Strategy, defaultValue: boolean): boolean {
    if (strategy.type === DnaStrategyType.MANUAL && isDefaultStrategyHasContextSettings(strategy)) {
        // enable_cpc_hold есть только у ручной стратегии
        return strategy.cpcHoldEnabled;
    }
    return defaultValue;
}

export function mapContextLimitToApiFormat(strategy: Strategy, defaultValue: number): number {
    if (strategy.type === DnaStrategyType.MANUAL && isDefaultStrategyHasContextSettings(strategy)) {
        // contextLimit есть только у ручной стратегии при показах в сетях и на поиске
        return strategy.contextLimit;
    }
    return defaultValue;
}

export function mapAttributionModelFromApiFormat(
    rawAttributionModel: GdCampaignAttributionModel | null
): AttributionModel {
    switch (rawAttributionModel) {
        case GdCampaignAttributionModel.FIRST_CLICK:
            return AttributionModel.FIRST_CLICK;

        case GdCampaignAttributionModel.LAST_CLICK:
            return AttributionModel.LAST_CLICK;

        case GdCampaignAttributionModel.LAST_SIGNIFICANT_CLICK:
            return AttributionModel.LAST_SIGNIFICANT_CLICK;

        case GdCampaignAttributionModel.LAST_YANDEX_DIRECT_CLICK:
        default:
            return AttributionModel.LAST_YANDEX_DIRECT_CLICK;
    }
}

export function mapAttributionModelToApiFormat(strategy: Strategy): GdCampaignAttributionModelInput {
    switch (strategy.attributionModel) {
        case AttributionModel.FIRST_CLICK:
            return GdCampaignAttributionModelInput.FIRST_CLICK;

        case AttributionModel.LAST_CLICK:
            return GdCampaignAttributionModelInput.LAST_CLICK;

        case AttributionModel.LAST_SIGNIFICANT_CLICK:
            return GdCampaignAttributionModelInput.LAST_SIGNIFICANT_CLICK;

        case AttributionModel.LAST_YANDEX_DIRECT_CLICK:
        default:
            return GdCampaignAttributionModelInput.LAST_YANDEX_DIRECT_CLICK;
    }
}

export function mapStrategyToApiFormat(
    strategy: Strategy,
    constants: EditCampaignsConstants
): GdCampaignBiddingStrategyInput {
    switch (strategy.type) {
        case DnaStrategyType.OPTIMIZE_CLICKS:
            return mapStrategyTypeOptimizeClicks(strategy);

        case DnaStrategyType.OPTIMIZE_CPA:
            return mapStrategyTypeOptimizeCpaToApiFormat(strategy, constants);

        case DnaStrategyType.ROI:
            return mapStrategyTypeRoiToApiFormat(strategy, constants);

        case DnaStrategyType.MANUAL:
            return mapStrategyTypeManualToApiFormat(strategy);

        default:
            throw new Error('unsupported strategy type!');
    }
}

function mapStrategyTypeRoiToApiFormat(
    strategy: StrategyRoi,
    constants: EditCampaignsConstants
): GdCampaignBiddingStrategyInput {
    return {
        strategy: getApiStrategyForAutobudgetStrategy(strategy),
        strategyName: GdCampaignStrategyNameInput.AUTOBUDGET_ROI,
        platform: mapCampaignPlatformToApiFormat(strategy.platform),
        strategyData: {
            goalId: mapGoalId(strategy.goalId, constants),
            roiCoef: getNumberForApiFormat(strategy.roiCoef),
            reserveReturn: getNumberForApiFormat(strategy.reserveReturn),
            sum: getNumberForApiFormat(strategy.weeklyBudget),
            bid: getNumberForApiFormat(strategy.maxClickPrice),
            profitability: getNumberForApiFormat(strategy.profitability)
        }
    };
}

function mapStrategyTypeManualToApiFormat(strategy: StrategyManual): GdCampaignBiddingStrategyInput {
    return {
        strategyName: GdCampaignStrategyNameInput.DEFAULT_,
        strategy:
            isDefaultStrategyHasSeparateBidding(strategy) || isContextPlatformsStrategy(strategy)
                ? GdCampaignStrategyInput.DIFFERENT_PLACES
                : null,
        platform: mapCampaignPlatformToApiFormat(strategy.platform),
        strategyData: {}
    };
}

function mapStrategyTypeOptimizeCpaToApiFormat(
    strategy: StrategyOptimizeCpa,
    constants: EditCampaignsConstants
): GdCampaignBiddingStrategyInput {
    let strategyName;
    let strategyData: GdCampaignStrategyDataInput;

    switch (strategy.optimizationCriteria) {
        case OptimizationCriteria.WEEKLY_BUDGET:
            strategyName = GdCampaignStrategyNameInput.AUTOBUDGET;
            strategyData = {
                goalId: mapGoalId(strategy.goalId, constants),
                sum: getNumberForApiFormat(strategy.weeklyBudget),
                bid: getNumberForApiFormat(strategy.maxClickPrice)
            };
            break;

        case OptimizationCriteria.AVG_CPA:
        default:
            strategyName = GdCampaignStrategyNameInput.AUTOBUDGET_AVG_CPA;
            strategyData = {
                goalId: mapGoalId(strategy.goalId, constants),
                avgCpa: getNumberForApiFormat(strategy.avgCpaPrice),
                sum: getNumberForApiFormat(strategy.weeklyBudget),
                payForConversion: strategy.isCpaPayForConversionEnabled
            };

            if (!strategy.isCpaPayForConversionEnabled) {
                strategyData.bid = getNumberForApiFormat(strategy.maxClickPrice);
            }
            break;
    }

    return {
        strategy: getApiStrategyForAutobudgetStrategy(strategy),
        strategyName,
        platform: mapCampaignPlatformToApiFormat(strategy.platform),
        strategyData
    };
}

function mapStrategyTypeOptimizeClicks(strategy: StrategyOptimizeClicks): GdCampaignBiddingStrategyInput {
    let strategyName;
    let strategyData: GdCampaignStrategyDataInput;

    switch (strategy.optimizationCriteria) {
        case OptimizationCriteria.AVG_CLICK:
            strategyName = GdCampaignStrategyNameInput.AUTOBUDGET_AVG_CLICK;
            strategyData = {
                avgBid: getNumberForApiFormat(strategy.avgClickPrice)
            };
            if (strategy.weeklyBudget) {
                strategyData.sum = getNumberForApiFormat(strategy.weeklyBudget);
            }
            break;

        case OptimizationCriteria.WEEK_BUNDLE:
            strategyName = GdCampaignStrategyNameInput.AUTOBUDGET_WEEK_BUNDLE;
            strategyData = {
                limitClicks: getNumberForApiFormat(strategy.weekBundle)
            };
            if (strategy.avgClickPrice) {
                strategyData.avgBid = getNumberForApiFormat(strategy.avgClickPrice);
            } else {
                strategyData.bid = getNumberForApiFormat(strategy.maxClickPrice);
            }
            break;

        case OptimizationCriteria.WEEKLY_BUDGET:
        default:
            strategyName = GdCampaignStrategyNameInput.AUTOBUDGET;
            strategyData = {
                sum: getNumberForApiFormat(strategy.weeklyBudget),
                bid: getNumberForApiFormat(strategy.maxClickPrice)
            };
            break;
    }

    return {
        strategy: getApiStrategyForAutobudgetStrategy(strategy),
        strategyName,
        platform: mapCampaignPlatformToApiFormat(strategy.platform),
        strategyData
    };
}

function getApiStrategyForAutobudgetStrategy(
    strategy: AutobudgetStrategy
): GdCampaignStrategyInput.DIFFERENT_PLACES | null {
    return isContextPlatformsStrategy(strategy) ? GdCampaignStrategyInput.DIFFERENT_PLACES : null;
}

function mapCampaignPlatformToApiFormat(platform: CampaignPlatform): GdCampaignPlatformInput {
    switch (platform) {
        case CampaignPlatform.BOTH:
            return GdCampaignPlatformInput.BOTH;
        case CampaignPlatform.SEARCH:
            return GdCampaignPlatformInput.SEARCH;
        case CampaignPlatform.CONTEXT:
            return GdCampaignPlatformInput.CONTEXT;
        default:
            throw new Error('unsupported platform type');
    }
}

function mapGoalId(goalId: string | AdditionalGoalIds, constants: EditCampaignsConstants): string | null {
    switch (goalId) {
        case AdditionalGoalIds.all:
            return constants.byAllGoalsGoalId;
        case AdditionalGoalIds.allMeaningfulGoals:
            return constants.meaningfulGoalsOptimizationGoalId;
        case AdditionalGoalIds.empty:
            return null;
        default:
            return goalId;
    }
}

export const mapDailyBudgetParamsForMetrika = (
    oldDailyBudget?: number,
    newDailyBudget?: number
): DailyBudgetMetrikaOperationsParams | undefined => {
    if (!oldDailyBudget) {
        // если в исходной стратегии дневного бюджета не было
        // цель достигать не надо
        return undefined;
    } else if (!newDailyBudget) {
        return {
            goal: 'COMPANY_DAILY_LIMIT_OFF',
            params: {
                beforeLimit: oldDailyBudget
            }
        };
    } else if (oldDailyBudget < newDailyBudget) {
        return {
            goal: 'DAILY_BUDGET_LIMIT_CAMPAIGN_INCREASE',
            params: {
                difference: newDailyBudget - oldDailyBudget
            }
        };
    } else if (oldDailyBudget > newDailyBudget) {
        return {
            goal: 'DAILY_BUDGET_LIMIT_CAMPAIGN_DECREASE',
            params: {
                difference: oldDailyBudget - newDailyBudget
            }
        };
    }
    return undefined;
};

export const mapDailyBudgetParamsForMetrikaFromCampaigns = (
    campaigns: Record<CampaignId, EditCampaignData>
): DailyBudgetMetrikaOperationsParams[] => {
    return values(campaigns)
        .map(({ value: { strategy: newStrategy }, serverValue: { strategy: oldStrategy } }: EditCampaignData):
            | DailyBudgetMetrikaOperationsParams
            | undefined => {
            if (newStrategy.type === DnaStrategyType.MANUAL && oldStrategy.type === DnaStrategyType.MANUAL) {
                // достигаем цели только если
                // обе стратегии ручные
                return mapDailyBudgetParamsForMetrika(oldStrategy.dailyBudget, newStrategy.dailyBudget);
            }
            return;
        })
        .filter(Boolean) as DailyBudgetMetrikaOperationsParams[];
};

export function mapCampaignsToFetchMetrikaGoalsForStrategyEditorInput(
    campaignsRecord: Record<CampaignId, EditCampaignData>
): Record<CampaignId, string[]> {
    return Object.keys(campaignsRecord).reduce((acc, campaignId: CampaignId): Record<CampaignId, EditCampaignData> => {
        const campaign = campaignsRecord[campaignId];

        if (campaign.enabledFields[EditCampaignValueField.strategy]) {
            acc[campaignId] = campaign.value[EditCampaignValueField.metrikaCounters];
        }

        return acc;
    }, {});
}
